// ============================================================================
// Équations trigonométriques (moteur v24, refonte lot 5.3 —
// docs/moteur/REFONTE_MOTEUR.md). Fonctions PURES, sans état de feuille.
// ============================================================================
// Dans une feuille `kernel.method = 'trig'`, on mesure l'avancée à la FORME de la
// ligne écrite :
//
//   deux fonctions (sin(x) = cos(x), cos(x) + sin(x) = 1)   50  diviser par cos(x),
//                                                                angle associé
//   cos(…) pas encore isolé (2sin(x) − 1 = 0)                40 + isolation de sin(x)
//   cos(…) face à un nombre (sin(x) = 1 ÷ 2)                 40  valeur remarquable
//   même fonction des deux côtés (sin(x) = sin(π ÷ 6),       35  familles, cercle
//     cos(x) = cos(π ÷ 2 − x))
//   familles (x = π ÷ 6 + 2kπ…) sur un intervalle           20 + isolation : garder
//     sur ℝ : c'est la réponse                               isolation
//   valeurs de x (x = π ÷ 6 ou x = 5π ÷ 6)                   isolation habituelle
//
// Un ensemble vaut sa clause la plus éloignée, plus un dixième des autres.
// ============================================================================

import { isSolvedSet } from './kernel-poly';
import { replaceKey, trigNodes } from './kernel/trig';
import {
  canon,
  relationsIn,
  sym,
  symbolsOf,
  termKey,
  type Relation,
  type Statement,
  type Term,
} from './kernel/term';

export type TrigForm =
  | 'mixed'
  | 'trig'
  | 'isolated'
  | 'reference'
  | 'associated'
  | 'family'
  | 'linear';

export const TRIG_FORM_H: Record<Exclude<TrigForm, 'trig' | 'family' | 'linear'>, number> & {
  trig: number;
  family: number;
} = {
  mixed: 50,
  trig: 40,
  isolated: 40,
  reference: 35,
  associated: 35,
  family: 20,
};

const has = (t: Term, x: string): boolean => symbolsOf(t).has(x);
const hasParam = (t: Term, params: ReadonlySet<string>): boolean =>
  [...symbolsOf(t)].some((s) => params.has(s));
const sameTerm = (a: Term, b: Term): boolean => termKey(canon(a)) === termKey(canon(b));

/** Les nœuds cos / sin / tan qui contiennent x, sans doublon. */
export function trigNodesOfRelation(r: Relation, x: string): Term[] {
  const out: Term[] = [];
  for (const n of [...trigNodes(r.l, x), ...trigNodes(r.r, x)])
    if (!out.some((m) => termKey(m) === termKey(n))) out.push(n);
  return out;
}

/** Forme d'une relation dans une feuille trigonométrique. */
export function trigRelForm(r: Relation, x: string, params: ReadonlySet<string>): TrigForm {
  const nodes = trigNodesOfRelation(r, x);
  if (!nodes.length) return hasParam(r.l, params) || hasParam(r.r, params) ? 'family' : 'linear';
  if (nodes.length === 1) {
    const n = nodes[0]!;
    const [node, other] = sameTerm(r.l, n)
      ? [r.l, r.r]
      : sameTerm(r.r, n)
        ? [r.r, r.l]
        : [null, null];
    if (!node || !other || has(other, x)) return 'trig';
    const o = canon(other);
    return o.k === 'fn' && n.k === 'fn' && o.f === n.f && symbolsOf(o).size === 0
      ? 'reference'
      : 'isolated';
  }
  if (nodes.length === 2) {
    const [a, b] = nodes as [Term, Term];
    const oneEach =
      (sameTerm(r.l, a) && sameTerm(r.r, b)) || (sameTerm(r.l, b) && sameTerm(r.r, a));
    if (oneEach && a.k === 'fn' && b.k === 'fn' && a.f === b.f) return 'associated';
  }
  return 'mixed';
}

/** Les clauses d'un énoncé (une relation, « ou », ensemble). */
export function trigClauses(s: Statement): Relation[][] {
  if (s.k === 'set') return s.clauses;
  if (s.k === 'or') return s.items.map((i) => [i]);
  if (s.k === 'rel') return [[{ rel: s.rel, l: s.l, r: s.r }]];
  return [];
}

/**
 * Distance d'une ligne à la réponse (feuille « trig »). `iso(r, v)` : l'isolation
 * habituelle de v dans r ; `bounded` : l'énoncé donne un intervalle (les familles
 * ne sont alors pas la réponse).
 */
export function trigH(
  s: Statement,
  x: string,
  params: ReadonlySet<string>,
  bounded: boolean,
  iso: (r: Relation, v: string) => number,
): number {
  const relH = (r: Relation): number => {
    const f = trigRelForm(r, x, params);
    switch (f) {
      case 'mixed':
      case 'isolated':
      case 'reference':
      case 'associated':
        return TRIG_FORM_H[f];
      case 'trig': {
        // isoler cos(…) comme une inconnue
        const node = trigNodesOfRelation(r, x)[0]!;
        const k = termKey(node);
        const slot = '§t';
        const rr = {
          rel: r.rel,
          l: replaceKey(r.l, k, sym(slot)),
          r: replaceKey(r.r, k, sym(slot)),
        };
        return TRIG_FORM_H.trig + iso(rr, slot);
      }
      case 'family':
        return (bounded ? TRIG_FORM_H.family : 0) + iso(r, x);
      case 'linear':
        return iso(r, x);
    }
  };
  const hs = trigClauses(s).map((c) => Math.max(...c.map(relH)));
  if (!hs.length) return TRIG_FORM_H.mixed;
  const max = Math.max(...hs);
  const sum = hs.reduce((a, b) => a + b, 0);
  return max + 0.1 * (sum - max) + 0.05 * (hs.length - 1);
}

/**
 * La forme d'une réponse : chaque clause « x = … » (familles permises sur ℝ, pas sur un
 * intervalle), ou une chaîne x face à des constantes (inéquation).
 */
export function trigSolved(
  s: Statement,
  x: string,
  params: ReadonlySet<string>,
  bounded: boolean,
): boolean {
  if (s.k === 'set')
    return (
      isSolvedSet(s, x) &&
      !relationsIn(s).some((r) => hasParam(r.l, params) || hasParam(r.r, params))
    );
  const rels = relationsIn(s);
  return (
    rels.length > 0 &&
    rels.every(
      (r) =>
        r.rel === '=' &&
        r.l.k === 'sym' &&
        r.l.n === x &&
        !has(r.r, x) &&
        (!bounded || !hasParam(r.r, params)) &&
        trigNodesOfRelation(r, x).length === 0,
    )
  );
}

/** La ligne écrit-elle un entier k (des familles) ? */
export function hasFamilies(s: Statement, params: ReadonlySet<string>): boolean {
  return relationsIn(s).some((r) => hasParam(r.l, params) || hasParam(r.r, params));
}
/** Aucune fonction trigonométrique de x, aucun k : des valeurs de x (ou une chaîne). */
export function isValueLine(s: Statement, x: string, params: ReadonlySet<string>): boolean {
  const rels = relationsIn(s);
  return (
    rels.length > 0 &&
    rels.every(
      (r) =>
        trigNodesOfRelation(r, x).length === 0 && !hasParam(r.l, params) && !hasParam(r.r, params),
    )
  );
}
